import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError, fileUrl, asArray } from "../../lib/api";
import FileUpload from "./FileUpload";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = {
    media:       { label: "Media Logos (Global Reach)", needsCategory: true, image_label: "Logo" },
};
const MEDIA_CATS = [
    { key: "europe", label: "Europe" },
    { key: "usa", label: "USA" },
    { key: "asia", label: "Asia" },
    { key: "middle_east", label: "Middle East" },
];
const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";

const blank = (section) => ({
    section, name_ar: "", name_en: "", image: "",
    category: section === "media" ? "europe" : "", link: "", order: 0, published: true,
});

export default function AdminBrandItems() {
    const { section: paramSection } = useParams();
    const section = SECTIONS[paramSection] ? paramSection : "media";
    const meta = SECTIONS[section];

    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try { setItems(asArray((await api.get(`/admin/brand-items?section=${section}`)).data, "items")); }
        catch (e) { toast.error(formatApiError(e)); }
    }, [section]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            const payload = { ...editing, section };
            payload.order = Number(payload.order) || 0;
            if (payload.id) await api.patch(`/admin/brand-items/${payload.id}`, payload);
            else await api.post("/admin/brand-items", payload);
            toast.success("Saved");
            setEditing(null); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try { await api.delete(`/admin/brand-items/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    if (editing) {
        const e = editing;
        return (
            <div data-testid="admin-brand-form">
                <button onClick={() => setEditing(null)} className="text-sm text-[#6B7280] mb-6 inline-flex items-center gap-2"><X size={14} /> Cancel</button>
                <h2 className="heading text-2xl tracking-tight mb-8 font-bold">{e.id ? "Edit" : "New"} — {meta.label}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8">
                    <div><label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Name (AR)</label><input className={inputCls} value={e.name_ar} onChange={(ev) => setEditing({ ...e, name_ar: ev.target.value })} /></div>
                    <div><label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Name (EN)</label><input className={inputCls} value={e.name_en} onChange={(ev) => setEditing({ ...e, name_en: ev.target.value })} /></div>
                    {meta.needsCategory && (
                        <div className="md:col-span-2">
                            <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Region</label>
                            <select className={inputCls} value={e.category} onChange={(ev) => setEditing({ ...e, category: ev.target.value })}>
                                {MEDIA_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="md:col-span-2">
                        <FileUpload value={e.image} onChange={(v) => setEditing({ ...e, image: v })} label={meta.image_label} testid="brand-image-upload" accept="image/*" />
                    </div>
                    <div><label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Link (optional)</label><input className={inputCls} value={e.link} onChange={(ev) => setEditing({ ...e, link: ev.target.value })} placeholder="https://..." /></div>
                    <div><label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Order</label><input type="number" className={inputCls} value={e.order} onChange={(ev) => setEditing({ ...e, order: ev.target.value })} /></div>
                    <label className="inline-flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={e.published} onChange={(ev) => setEditing({ ...e, published: ev.target.checked })} /> Show on site</label>
                </div>
                <div className="mt-6"><button onClick={save} className="bg-[#002951] hover:bg-[#0060A7] text-white px-8 py-3 text-sm uppercase tracking-[0.2em]" data-testid="brand-save">Save</button></div>
            </div>
        );
    }

    return (
        <div data-testid="admin-brand-page">
            <div className="flex items-center justify-between mb-6">
                <h1 className="heading text-2xl tracking-tight font-bold">{meta.label}</h1>
                <button onClick={() => setEditing(blank(section))} data-testid="add-brand-btn" className="bg-[#002951] hover:bg-[#0060A7] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus size={14} /> New Item</button>
            </div>
            <div className="bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#EFF0F1] text-left text-xs uppercase tracking-[0.2em] text-[#6B7280]"><tr><th className="p-3 w-16">Image</th><th className="p-3">Name</th>{meta.needsCategory && <th className="p-3">Region</th>}<th className="p-3">Order</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#6B7280]">No items yet</td></tr>}
                        {items.map((it) => (
                            <tr key={it.id} className="border-t border-black/5">
                                <td className="p-3">
                                    <div className="w-12 h-12 bg-[#EFF0F1] flex items-center justify-center overflow-hidden">
                                        {it.image ? <img src={fileUrl(it.image)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-[#6B7280]">—</span>}
                                    </div>
                                </td>
                                <td className="p-3"><div className="heading">{it.name_en}</div><div className="text-xs text-[#6B7280]">{it.name_ar}</div></td>
                                {meta.needsCategory && <td className="p-3 text-[#6B7280]">{it.category}</td>}
                                <td className="p-3 text-[#6B7280]">{it.order}</td>
                                <td className="p-3">{it.published ? <span className="text-emerald-600 text-xs">Visible</span> : <span className="text-[#6B7280] text-xs">Hidden</span>}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(it)} className="mr-3 text-[#0060A7]"><Edit3 size={16} /></button>
                                    <button onClick={() => remove(it.id)} className="text-red-500"><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
